import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Table } from "antd";
import dayjs from "dayjs";
import { Users, List as ListIcon, Tool } from "react-feather";
import InfoTile from "../../../components/common/InfoTile";
import StatusBadge from "../../../components/common/StatusBadge";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetEmployeesQuery } from "../../masters/employees/employeesApi";
import { useCanSearchEmployees } from "../useTechnicalPermissions";
import { TECHNICAL_STATUS_CLASS, TECHNICAL_STATUS_LABEL, TECHNICAL_SOURCE_CLASS } from "../technicalStatusMaps";
import { useGetTechnicalDutyReportQuery } from "../technicalApi";
import { useListOrdersQuery } from "../../sales/salesApi";
import SalesLinkedDocumentsPanel from "../../sales/components/SalesLinkedDocumentsPanel";
import { INSTALLATION_STATUS_LABEL } from "../../sales/salesStatusMaps";
import { paths } from "../../../routes/paths";

// Technician Duty Report (Reports -> Technical Work, 2026-09-11, Batch159).
//
// Two halves, both folded client-side from ONE fetch of (technician, job) rows:
//  - Summary: one line per technician, attended counts per type + total -
//    the web version of the legacy WinForms "Daily Technical Report".
//  - Detail: one line per job - who went where, for what, and where it
//    stands - grouped by status by default, the web version of the legacy
//    "Daily Work Report As On" (Started / On Hold sections).
// Technician, customer and search filters therefore apply instantly to both.
//
// Dating (decided server-side, see the batch header): an attended job is
// dated by its completion date; a job still open is dated by its assignment
// and CARRIED FORWARD - it stays on every report until it is done, the way the
// legacy work report kept listing a job from its start date onwards.

// The user's own words for the three types ("service request, service visit
// and installations"). A COMPLAINT is what the legacy report counted as C/R.
const TYPE_LABEL = { SERVICE_VISIT: "Service Visit", COMPLAINT: "Service Request", SALES_ORDER: "Installation" };

// Installation Status section export - mirrors the on-screen columns.
const INSTALLATION_EXPORT_COLUMNS = [
  { header: "Customer", value: (r) => r.customerName || "" },
  { header: "Order", value: (r) => r.orderNumber || "" },
  { header: "Branch", value: (r) => r.branchCode || "" },
  { header: "Started", value: (r) => (r.startedDate ? dayjs(r.startedDate).format("DD-MMM-YY") : "") },
  { header: "Expected end", value: (r) => (r.expectedEndDate ? dayjs(r.expectedEndDate).format("DD-MMM-YY") : "") },
  { header: "Overdue", value: (r) => (r.overdue ? "Yes" : "") },
  { header: "Technicians", value: (r) => r.technicians.join(", ") },
  { header: "No. of technicians", align: "right", value: (r) => r.technicianCount },
  { header: "Status", value: (r) => (r.status ? INSTALLATION_STATUS_LABEL[r.status] || r.status : "Not recorded") },
  { header: "Days elapsed", align: "right", value: (r) => (r.elapsed === null ? "" : r.elapsed) },
  { header: "Remarks", value: (r) => r.remark || "" },
];
const TYPE_ORDER = ["Service Visit", "Service Request", "Installation"];

const fmtDate = (v) => (v ? dayjs(v).format("DD-MMM-YY") : "");
const fmtDateTime = (v) => (v ? dayjs(v).format("DD-MMM-YY h:mm A") : "");
const daysBetween = (from, to) =>
  from && to ? Math.max(0, dayjs(to).startOf("day").diff(dayjs(from).startOf("day"), "day")) : null;
const daysLabel = (n) => (n === null ? "" : n === 0 ? "same day" : `${n} day${n === 1 ? "" : "s"}`);
const clean = (s) => (s || "").trim();
const customerKey = (r) => `${r.branchId}-${r.custId}`;
const jobKey = (r) => r.technicalWorkRecordGuid;

// Where a job stands, in words a manager reads off the printout. CLOSED splits
// in two: closed after the technician completed it (verified), or closed
// without ever being attended (e.g. the 10-Sep housekeeping close).
const stateLabel = (r) => {
  if (r.dutyState === "CLOSED_UNATTENDED") return "Closed - not attended";
  switch (r.status) {
    case "ONGOING": return "Started";
    case "ASSIGNED": return "Assigned - not yet done";
    case "NOT_STARTED": return "Not started";
    case "ON_HOLD": return "On hold";
    case "COMPLETED": return "Completed - awaiting verification";
    case "CLOSED": return "Closed - verified";
    default: return TECHNICAL_STATUS_LABEL[r.status] || r.status;
  }
};
// Open work first (as the legacy report put Started above On Hold), then done.
const STATE_ORDER = [
  "Started",
  "Assigned - not yet done",
  "Not started",
  "On hold",
  "Completed - awaiting verification",
  "Closed - verified",
  "Closed - not attended",
];

// The technician's own words beat the manager's completion note, which beats
// the hold/closing note.
const outcomeOf = (r) => clean(r.techComment) || clean(r.completedRemarks) || clean(r.statusRemarks);

const SHOW_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "ATTENDED", label: "Attended" },
  { value: "OPEN", label: "Still open" },
  { value: "CLOSED_UNATTENDED", label: "Closed - not attended" },
];

const GROUP_OPTIONS = [
  { value: "STATUS", label: "Group by status" },
  { value: "TECHNICIAN", label: "Group by technician" },
  { value: "DATE", label: "Group by date" },
  { value: "TYPE", label: "Group by type" },
  { value: "CUSTOMER", label: "Group by customer" },
  { value: "NONE", label: "No grouping" },
];

const dutyDateCaption = { ATTENDED: "completed", OPEN: "assigned", CLOSED_UNATTENDED: "closed" };

const clampStyle = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  whiteSpace: "normal",
});

const selectStyles = {
  container: (base) => ({ ...base, minWidth: 240 }),
  control: (base) => ({ ...base, minHeight: 34 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

// Up to two most-visited locations, "+n" for the rest - the legacy report's
// per-technician "Area" column, taken from where they actually went.
const topAreas = (counts) => {
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  if (sorted.length === 0) return "";
  const head = sorted.slice(0, 2).map(([area]) => area).join(", ");
  return sorted.length > 2 ? `${head} +${sorted.length - 2}` : head;
};

const Count = ({ value, strong = false }) => (
  <span
    style={{ fontVariantNumeric: "tabular-nums", fontWeight: strong ? 700 : 400 }}
    className={value ? "" : "text-muted"}
  >
    {value}
  </span>
);

const Fact = ({ label, children }) =>
  children ? (
    <div className="mb-2">
      <div className="text-uppercase text-muted" style={{ fontSize: 10, letterSpacing: 0.4 }}>{label}</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{children}</div>
    </div>
  ) : null;

// Everything the row itself had to shorten, in full - "more information if
// needed" without making every line of the report tall.
const JobDetail = ({ row }) => {
  const taken = row.dutyState === "ATTENDED" ? daysBetween(row.assignedDate, row.completedDate) : null;
  return (
    <div className="row g-3 px-2 py-1" style={{ fontSize: 13 }}>
      <div className="col-md-6">
        <Fact label="Purpose">{clean(row.workDescription)}</Fact>
        <Fact label="Technician's report">
          {clean(row.techComment) &&
            `${clean(row.techComment)}${row.reportFiledAt ? `\n(filed ${fmtDateTime(row.reportFiledAt)})` : ""}`}
        </Fact>
        <Fact label="Completion remarks">{clean(row.completedRemarks)}</Fact>
        <Fact label={row.status === "ON_HOLD" ? "Hold reason" : row.status === "ONGOING" ? "Start remarks" : "Closing remarks"}>
          {clean(row.statusRemarks)}
        </Fact>
      </div>
      <div className="col-md-6">
        <Fact label="Follow-up of">{row.linkedFromWorkNumber}</Fact>
        <Fact label="Sent again as">{row.followUpWorkNumbers}</Fact>
        <Fact label="Technicians">{row.allTechnicians}</Fact>
        <Fact label="Customer">
          {[row.customerName, clean(row.customerAddress), clean(row.customerPhone)].filter(Boolean).join("\n")}
        </Fact>
        <Fact label="Timeline">
          {[
            `Logged ${fmtDateTime(row.createdDate)}`,
            row.assignedDate && `Assigned ${fmtDate(row.assignedDate)}`,
            row.startedDate && `Started ${fmtDate(row.startedDate)}`,
            row.completedDate && `Completed ${fmtDate(row.completedDate)}${taken !== null ? ` (${daysLabel(taken)})` : ""}`,
            row.closedDate && `Closed ${fmtDate(row.closedDate)}`,
          ]
            .filter(Boolean)
            .join("\n")}
        </Fact>
        <Fact label="Branch">{row.branchCode}</Fact>
      </div>
    </div>
  );
};

const TechnicianDutyReportPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("TODAY");
  const [technician, setTechnician] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [search, setSearch] = useState("");
  const [workType, setWorkType] = useState("");
  const [show, setShow] = useState("ALL");
  const [groupBy, setGroupBy] = useState("STATUS");
  const [includeIdle, setIncludeIdle] = useState(false);

  const { data: rows = [], isFetching, isError, error } = useGetTechnicalDutyReportQuery(
    { branchId: selectedBranchId ?? undefined, dateFrom, dateTo },
    { skip: !dateFrom || !dateTo }
  );

  // Technicians with nothing on their list don't appear in the duty rows at
  // all, so "who had no work" needs the TECHNICAL department itself - only
  // fetched when asked for, and only for accounts allowed to list employees.
  const canListStaff = useCanSearchEmployees();
  const { data: technicalStaff = [] } = useGetEmployeesQuery(
    { branchId: selectedBranchId ?? undefined, departmentNameLike: "TECHNICAL", isActivate: "true" },
    { skip: !canListStaff || !includeIdle }
  );

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const isAllBranches = !selectedBranchId;

  // ---- Filter options, from what's actually in the range ----
  const technicianOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) =>
      map.set(r.employeeRecordGuid, { value: r.employeeRecordGuid, label: `${r.employeeName} · ${r.employeeCode}` })
    );
    if (includeIdle) {
      technicalStaff.forEach((e) => {
        if (!map.has(e.recordGuid)) {
          const name = `${clean(e.empName)} ${clean(e.empLastName)}`.trim();
          map.set(e.recordGuid, { value: e.recordGuid, label: `${name} · ${e.empCode}` });
        }
      });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, includeIdle, technicalStaff]);

  const customerOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const place = clean(r.customerAddress);
      map.set(customerKey(r), {
        value: customerKey(r),
        label: `${r.customerName}${place ? ` · ${place}` : ""}${isAllBranches ? ` · ${r.branchCode}` : ""}`,
      });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, isAllBranches]);

  // A picked technician/customer that is no longer in the new range's data
  // stays selected (so changing the date doesn't silently drop the filter).
  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!workType || r.sourceType === workType) &&
          (!technician || r.employeeRecordGuid === technician.value) &&
          (!customer || customerKey(r) === customer.value) &&
          (!q ||
            [r.workNumber, r.customerName, r.customerAddress, r.customerPhone, r.workDescription, r.techComment,
              r.completedRemarks, r.statusRemarks, r.allTechnicians, r.linkedFromWorkNumber, r.followUpWorkNumbers]
              .some((f) => f && f.toLowerCase().includes(q)))
      ),
    [rows, workType, technician, customer, q]
  );

  // One line per job (a job two technicians shared is one visit).
  const jobs = useMemo(() => {
    const seen = new Map();
    filtered.forEach((r) => {
      if (!seen.has(jobKey(r))) seen.set(jobKey(r), r);
    });
    return [...seen.values()];
  }, [filtered]);

  // ---- Installations: a live status board, deliberately NOT period-scoped ----
  //
  // An "installation" here is a SALES ORDER carrying any service other than
  // guarding (user's own definition). The installation record hangs off the
  // order (PUT /sales/orders/{guid}/installation) - NOT off technical work, so
  // this does not come from the duty rows above. Everything the board needs is
  // already on the order list row, so no API or DB change was involved.
  //
  // Non-guarding is read off the order's own figures rather than re-querying
  // service types: an order with alarms or other-services money on it has
  // something to install. Verified against all 17 live orders - the 9 pure
  // GUARDS ones have both of these at zero, the 8 ALARMS/OTHER ones do not.
  // An order mixing guards WITH alarms still counts, which is what "includes
  // types other than guarding" asks for.
  //
  // Not filtered by the report's date range on purpose: "where does each
  // installation stand right now" is a current-state question, and a job
  // started before the range would otherwise vanish mid-installation. Said out
  // loud under the table so nobody reads it as a period figure.
  const { data: orderPage, isFetching: isFetchingInstallations } = useListOrdersQuery({
    branchId: selectedBranchId ?? undefined,
    pageSize: 500,
  });

  const installations = useMemo(() => {
    // technicianEmployeeIds is hand-typed free text ("Comma-separated employee
    // IDs"), so resolve what we can against the staff list and show the raw
    // token for anything that does not match rather than dropping it.
    const nameByCode = new Map();
    technicalStaff.forEach((e) => {
      const name = `${clean(e.empName)} ${clean(e.empLastName)}`.trim();
      if (e.empCode != null && name) nameByCode.set(String(e.empCode), name);
    });

    return (orderPage?.items || [])
      .filter((o) => Number(o.alarmsCount) > 0 || Number(o.otherServicesAmount) > 0)
      .map((o) => {
        const techs = clean(o.technicianEmployeeIds)
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean)
          .map((t) => nameByCode.get(t) || t);

        // Elapsed runs from the start to whenever the job actually stopped, and
        // to today only while it is still running. Measuring a finished job
        // against today would inflate for ever just because nobody reopened the
        // report.
        const endedAt = o.completedDate || null;
        const elapsed = o.startedDate ? daysBetween(o.startedDate, endedAt || new Date()) : null;
        const overdue = !endedAt && o.expectedEndDate && dayjs().isAfter(dayjs(o.expectedEndDate), "day");
        return {
          key: o.recordGuid,
          recordGuid: o.recordGuid,
          // Quotations/LPOs/stock-outs all hang off the ORIGINATING ENQUIRY,
          // not the order - that is the key SalesLinkedDocumentsPanel wants.
          enquiryRecordGuid: o.enquiryRecordGuid,
          orderNumber: o.orderNumber,
          customerName: o.customerName,
          branchCode: o.branchCode,
          startedDate: o.startedDate,
          expectedEndDate: o.expectedEndDate,
          status: o.installationStatus,
          technicians: techs,
          technicianCount: techs.length,
          elapsed,
          isRunning: !endedAt,
          overdue,
          remark: clean(o.onHoldRemarks),
        };
      })
      .sort((a, b) => {
        // Running jobs first, longest-running at the top - that is the queue a
        // manager works down; finished ones are reference.
        if (a.isRunning !== b.isRunning) return a.isRunning ? -1 : 1;
        return (b.elapsed ?? -1) - (a.elapsed ?? -1);
      });
  }, [orderPage, technicalStaff]);

  // ---- Summary: one line per technician ----
  const summary = useMemo(() => {
    const map = new Map();
    const blank = (guid, code, name) => ({
      key: guid, employeeRecordGuid: guid, employeeCode: code, name,
      serviceVisits: 0, serviceRequests: 0, installations: 0, attended: 0, open: 0, closedUnattended: 0,
      daysTotal: 0, daysCount: 0, areaCounts: new Map(),
    });
    // Idle staff only make sense on an unfiltered view - "had no work for
    // customer X" is true of almost everyone and says nothing.
    if (includeIdle && !customer && !q) {
      technicalStaff
        .filter((e) => !technician || e.recordGuid === technician.value)
        .forEach((e) =>
          map.set(e.recordGuid, blank(e.recordGuid, e.empCode, `${clean(e.empName)} ${clean(e.empLastName)}`.trim()))
        );
    }
    filtered.forEach((r) => {
      if (!map.has(r.employeeRecordGuid)) map.set(r.employeeRecordGuid, blank(r.employeeRecordGuid, r.employeeCode, r.employeeName));
      const s = map.get(r.employeeRecordGuid);
      const area = clean(r.customerAddress).toUpperCase();
      if (area) s.areaCounts.set(area, (s.areaCounts.get(area) || 0) + 1);
      if (r.dutyState === "ATTENDED") {
        s.attended += 1;
        if (r.sourceType === "SERVICE_VISIT") s.serviceVisits += 1;
        else if (r.sourceType === "COMPLAINT") s.serviceRequests += 1;
        else if (r.sourceType === "SALES_ORDER") s.installations += 1;
        const d = daysBetween(r.assignedDate, r.completedDate);
        if (d !== null) {
          s.daysTotal += d;
          s.daysCount += 1;
        }
      } else if (r.dutyState === "OPEN") s.open += 1;
      else s.closedUnattended += 1;
    });
    return [...map.values()]
      .map((s) => ({ ...s, areas: topAreas(s.areaCounts), avgDays: s.daysCount ? s.daysTotal / s.daysCount : null }))
      .sort((a, b) => b.attended - a.attended || b.open - a.open || a.name.localeCompare(b.name));
  }, [filtered, includeIdle, technicalStaff, technician, customer, q]);

  const summaryTotals = useMemo(
    () =>
      summary.reduce(
        (t, s) => ({
          serviceVisits: t.serviceVisits + s.serviceVisits,
          serviceRequests: t.serviceRequests + s.serviceRequests,
          installations: t.installations + s.installations,
          attended: t.attended + s.attended,
          open: t.open + s.open,
        }),
        { serviceVisits: 0, serviceRequests: 0, installations: 0, attended: 0, open: 0 }
      ),
    [summary]
  );

  // ---- Headline tiles: each JOB once ----
  //
  // The strip is also the filter (2026-09-21, "keep this strip as it is and
  // click to filter, no additional controls"): the three type tiles set the
  // work type, "Jobs attended" / "Still open" set which jobs are listed, and a
  // second click clears. So the figures are counted WITHOUT the type filter -
  // otherwise picking one type would zero the other tiles and leave nothing to
  // click back to. Technician, customer and search still narrow them.
  const tiles = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (technician && r.employeeRecordGuid !== technician.value) return;
      if (customer && customerKey(r) !== customer.value) return;
      if (q && ![r.workNumber, r.customerName, r.customerAddress, r.customerPhone, r.workDescription, r.techComment,
        r.completedRemarks, r.statusRemarks, r.allTechnicians, r.linkedFromWorkNumber, r.followUpWorkNumbers]
        .some((f) => f && f.toLowerCase().includes(q))) return;
      if (!seen.has(jobKey(r))) seen.set(jobKey(r), r);
    });
    const all = [...seen.values()];
    const attended = all.filter((j) => j.dutyState === "ATTENDED");
    return {
      attended: attended.length,
      serviceVisits: attended.filter((j) => j.sourceType === "SERVICE_VISIT").length,
      serviceRequests: attended.filter((j) => j.sourceType === "COMPLAINT").length,
      installations: attended.filter((j) => j.sourceType === "SALES_ORDER").length,
      open: all.filter((j) => j.dutyState === "OPEN").length,
      technicians: new Set(filtered.map((r) => r.employeeRecordGuid)).size,
    };
  }, [rows, technician, customer, q, filtered]);

  const toggleType = (type) => setWorkType((cur) => (cur === type ? "" : type));
  const toggleShow = (value) => setShow((cur) => (cur === value ? "ALL" : value));

  // ---- Detail ----
  const detailBase = groupBy === "TECHNICIAN" ? filtered : jobs;
  const showCounts = useMemo(() => {
    const c = { ALL: detailBase.length, ATTENDED: 0, OPEN: 0, CLOSED_UNATTENDED: 0 };
    detailBase.forEach((r) => {
      c[r.dutyState] += 1;
    });
    return c;
  }, [detailBase]);

  const detailRows = useMemo(
    () => (show === "ALL" ? detailBase : detailBase.filter((r) => r.dutyState === show)),
    [detailBase, show]
  );

  const groupKeyFn = useMemo(() => {
    switch (groupBy) {
      case "STATUS": return stateLabel;
      case "TECHNICIAN": return (r) => r.employeeName;
      case "DATE": return (r) => dayjs(r.dutyDate).format("ddd DD-MMM-YYYY");
      case "TYPE": return (r) => TYPE_LABEL[r.sourceType] || r.sourceType;
      case "CUSTOMER": return (r) => r.customerName;
      default: return null;
    }
  }, [groupBy]);

  const groupOrder = useMemo(() => {
    if (groupBy === "STATUS") return STATE_ORDER;
    if (groupBy === "TYPE") return TYPE_ORDER;
    if (groupBy === "DATE") {
      const dates = [...new Set(detailRows.map((r) => r.dutyDate))].sort((a, b) => (a < b ? 1 : -1));
      return dates.map((d) => dayjs(d).format("ddd DD-MMM-YYYY"));
    }
    return undefined;
  }, [groupBy, detailRows]);

  // Rows in final display order, each numbered within its group (the printed
  // report's "No." column, as the legacy one had).
  const groupedDetail = useMemo(() => {
    const byNewest = [...detailRows].sort(
      (a, b) => (a.dutyDate < b.dutyDate ? 1 : a.dutyDate > b.dutyDate ? -1 : a.workNumber.localeCompare(b.workNumber))
    );
    if (!groupKeyFn) return [{ key: null, rows: byNewest.map((r, i) => ({ ...r, serial: i + 1 })) }];
    const map = new Map();
    byNewest.forEach((r) => {
      const key = groupKeyFn(r) || "-";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    const rank = (k) => {
      const i = groupOrder ? groupOrder.indexOf(k) : -1;
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...map.keys()]
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
      .map((key) => ({ key, rows: map.get(key).map((r, i) => ({ ...r, serial: i + 1 })) }));
  }, [detailRows, groupKeyFn, groupOrder]);

  // One antd table with a full-width heading row per group - many small
  // tables would repeat the header dozens of times when grouping by customer.
  const detailTableData = useMemo(
    () =>
      groupedDetail.flatMap(({ key, rows: groupRows }) => [
        ...(key === null ? [] : [{ __group: key, count: groupRows.length, rowKey: `group-${key}` }]),
        ...groupRows.map((r) => ({ ...r, rowKey: `${jobKey(r)}-${r.employeeRecordGuid}` })),
      ]),
    [groupedDetail]
  );
  const exportDetailRows = useMemo(() => groupedDetail.flatMap((g) => g.rows), [groupedDetail]);

  // ---- Wording for the headings / printouts ----
  const isSingleDay = dateFrom === dateTo;
  const dateLine = isSingleDay
    ? `Date: ${dayjs(dateFrom).format("DD-MMM-YYYY")}`
    : `From ${dayjs(dateFrom).format("DD-MMM-YYYY")} to ${dayjs(dateTo).format("DD-MMM-YYYY")}`;
  const filterLines = [
    `Branch: ${branchCode || "All"}`,
    dateLine,
    workType && `Type: ${TYPE_LABEL[workType]}`,
    technician && `Technician: ${technician.label}`,
    customer && `Customer: ${customer.label}`,
    q && `Search: "${search.trim()}"`,
  ].filter(Boolean);
  const stamp = dayjs(dateTo).format("YYYYMMDD");

  const summaryColumnsDef = [
    {
      title: "Technician",
      dataIndex: "name",
      render: (v, s) => (
        <>
          <div className="fw-semibold">{v}</div>
          <div className="text-muted small">Code {s.employeeCode}</div>
        </>
      ),
    },
    { title: "Areas visited", dataIndex: "areas", render: (v) => v || <span className="text-muted">-</span> },
    { title: "Service Visits", dataIndex: "serviceVisits", align: "right", render: (v) => <Count value={v} /> },
    { title: "Service Requests", dataIndex: "serviceRequests", align: "right", render: (v) => <Count value={v} /> },
    { title: "Installations", dataIndex: "installations", align: "right", render: (v) => <Count value={v} /> },
    { title: "Total attended", dataIndex: "attended", align: "right", render: (v) => <Count value={v} strong /> },
    {
      title: "Still open",
      dataIndex: "open",
      align: "right",
      render: (v) => (v ? <span className="text-warning fw-semibold">{v}</span> : <Count value={0} />),
    },
    {
      title: "Avg. time to complete",
      dataIndex: "avgDays",
      align: "right",
      render: (v) =>
        v === null ? <span className="text-muted">-</span> : v < 0.5 ? "same day" : `${v.toFixed(1)} days`,
    },
  ];

  const summaryExportColumns = [
    { header: "Technician", value: (s) => s.name },
    { header: "Areas visited", value: (s) => s.areas },
    { header: "Service Visits", value: (s) => s.serviceVisits, align: "right" },
    { header: "Service Requests", value: (s) => s.serviceRequests, align: "right" },
    { header: "Installations", value: (s) => s.installations, align: "right" },
    { header: "Total", value: (s) => s.attended, align: "right" },
    { header: "Still Open", value: (s) => s.open, align: "right" },
    // Blank on purpose - the legacy printout kept a Remarks column for the
    // supervisor's own notes before signing.
    { header: "Remarks", value: () => "" },
  ];
  const summaryExportTotals = {
    Technician: "Total",
    "Service Visits": summaryTotals.serviceVisits,
    "Service Requests": summaryTotals.serviceRequests,
    Installations: summaryTotals.installations,
    Total: summaryTotals.attended,
    "Still Open": summaryTotals.open,
  };

  const colCount = 9;
  const groupCell = (r, i) => (r.__group ? { colSpan: i === 0 ? colCount : 0 } : {});
  const detailColumnsDef = [
    {
      title: "Date",
      dataIndex: "dutyDate",
      width: 92,
      render: (v, r) =>
        r.__group ? (
          <div className="fw-bold" style={{ color: "#6E1423" }}>
            {r.__group} <span className="text-muted fw-normal ms-1">({r.count})</span>
          </div>
        ) : (
          <>
            <div style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(v)}</div>
            <div className="text-muted small">{dutyDateCaption[r.dutyState]}</div>
          </>
        ),
    },
    {
      title: "Work No",
      dataIndex: "workNumber",
      render: (v, r) => (
        <>
          <Link to={paths.technicalWorkDetail(r.technicalWorkRecordGuid)}>{v}</Link>
          {isAllBranches && <div className="text-muted small">{r.branchCode}</div>}
          {r.linkedFromWorkNumber && <div className="text-muted small">follow-up of {r.linkedFromWorkNumber}</div>}
        </>
      ),
    },
    {
      title: "Type",
      dataIndex: "sourceType",
      render: (v) => <span className={`badge ${TECHNICAL_SOURCE_CLASS[v] || "badge-linesecondary"}`}>{TYPE_LABEL[v] || v}</span>,
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      render: (v, r) => (
        <>
          <div className="fw-semibold">{v}</div>
          {clean(r.customerPhone) && <div className="text-muted small">{r.customerPhone}</div>}
        </>
      ),
    },
    { title: "Location", dataIndex: "customerAddress", render: (v) => clean(v) || <span className="text-muted">-</span> },
    {
      title: "Purpose",
      dataIndex: "workDescription",
      width: 220,
      render: (v) => <div style={clampStyle(2)} title={clean(v)}>{clean(v) || <span className="text-muted">-</span>}</div>,
    },
    {
      title: "Technicians",
      dataIndex: "allTechnicians",
      width: 170,
      render: (v) => <div style={{ whiteSpace: "normal" }}>{v || "-"}</div>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v, r) => (
        <>
          <StatusBadge status={v} classMap={TECHNICAL_STATUS_CLASS} labelMap={TECHNICAL_STATUS_LABEL} />
          <div className="text-muted small mt-1">
            {r.dutyState === "OPEN" && `open ${daysLabel(daysBetween(r.assignedDate, dayjs()))}`}
            {r.dutyState === "ATTENDED" && `took ${daysLabel(daysBetween(r.assignedDate, r.completedDate))}`}
            {r.dutyState === "CLOSED_UNATTENDED" && "not attended"}
          </div>
          {/* A finished job is never reopened - sending someone again after the
              customer check raises a linked follow-up job, named here. */}
          {r.followUpWorkNumbers && (
            <div className="small fw-semibold" style={{ color: "#6E1423" }}>sent again: {r.followUpWorkNumbers}</div>
          )}
        </>
      ),
    },
    {
      title: "Outcome / remarks",
      dataIndex: "techComment",
      width: 220,
      render: (_, r) => {
        const text = outcomeOf(r);
        return <div style={clampStyle(2)} title={text}>{text || <span className="text-muted">-</span>}</div>;
      },
    },
  ].map((c, i) => ({ ...c, onCell: (r) => groupCell(r, i) }));

  const detailExportColumns = [
    { header: "No.", value: (r) => r.serial },
    { header: "Date", value: (r) => fmtDate(r.dutyDate) },
    { header: "Work No", value: (r) => (r.linkedFromWorkNumber ? `${r.workNumber} (follow-up of ${r.linkedFromWorkNumber})` : r.workNumber) },
    { header: "Type", value: (r) => TYPE_LABEL[r.sourceType] || r.sourceType },
    { header: "Customer", value: (r) => r.customerName },
    { header: "Location", value: (r) => clean(r.customerAddress) },
    { header: "Purpose", value: (r) => clean(r.workDescription) },
    { header: groupBy === "TECHNICIAN" ? "Crew" : "Technicians", value: (r) => r.allTechnicians || "" },
    { header: "Status", value: (r) => (r.followUpWorkNumbers ? `${stateLabel(r)} - sent again: ${r.followUpWorkNumbers}` : stateLabel(r)) },
    { header: "Assigned", value: (r) => fmtDate(r.assignedDate) },
    { header: "Completed", value: (r) => fmtDate(r.completedDate) },
    { header: "Remarks", value: (r) => outcomeOf(r) },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Technician Duty Report</h4>
              <h6>Who attended what, where, and where each job stands</h6>
            </div>
          </div>
        </div>

        {/* ---- Filters: one bar, applies to both halves ---- */}
        <div className="card">
          <div className="card-body d-flex flex-column gap-2">
            <div className="d-flex flex-wrap align-items-start gap-3">
              <DateRangePicker
                preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
              />
              <small className="text-muted" style={{ maxWidth: 360 }}>
                Attended jobs count on the day they were completed. A job that is still open is listed on every
                report until it is done, whatever day it was assigned.
              </small>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Select
                classNamePrefix="react-select"
                styles={selectStyles}
                isClearable
                options={technicianOptions}
                value={technician}
                onChange={setTechnician}
                placeholder="All technicians - type to search"
                noOptionsMessage={() => "No technician had work in this range"}
              />
              <Select
                classNamePrefix="react-select"
                styles={{ ...selectStyles, container: (base) => ({ ...base, minWidth: 300 }) }}
                isClearable
                options={customerOptions}
                value={customer}
                onChange={setCustomer}
                placeholder="All customers - type to search"
                noOptionsMessage={() => "No customer had a visit in this range"}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 240, height: 34 }}
                placeholder="Work no., location, purpose, remarks"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {canListStaff && (
                <div className="form-check form-switch ms-1 mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="dutyIncludeIdle"
                    checked={includeIdle}
                    onChange={(e) => setIncludeIdle(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="dutyIncludeIdle">
                    Also list technicians with no work
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {isError && (
          <div className="alert alert-danger">Could not load the report ({error?.message || "server error"}).</div>
        )}

        {/* ---- Headline: each job once ---- */}
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-4 col-xl-2">
            <InfoTile label="Jobs attended" tone="maroon" value={tiles.attended}
              active={show === "ATTENDED"} onClick={() => toggleShow("ATTENDED")} title="Click to list attended jobs only" />
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <InfoTile label="Service visits" value={tiles.serviceVisits}
              active={workType === "SERVICE_VISIT"} onClick={() => toggleType("SERVICE_VISIT")} title="Click to show service visits only" />
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <InfoTile label="Service requests" value={tiles.serviceRequests}
              active={workType === "COMPLAINT"} onClick={() => toggleType("COMPLAINT")} title="Click to show service requests (complaints) only" />
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <InfoTile label="Installations" value={tiles.installations}
              active={workType === "SALES_ORDER"} onClick={() => toggleType("SALES_ORDER")} title="Click to show installations only" />
          </div>
          <div className="col-6 col-md-4 col-xl-2">
            <InfoTile label="Still open" value={tiles.open}
              active={show === "OPEN"} onClick={() => toggleShow("OPEN")} title="Click to list jobs still open only" />
          </div>
          <div className="col-6 col-md-4 col-xl-2"><InfoTile label="Technicians on duty" tone="success" value={tiles.technicians} /></div>
        </div>

        {/* ---- Summary: the "Daily Technical Report" ---- */}
        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h5 className="mb-0 d-inline-flex align-items-center gap-2"><Users size={18} /> Summary by technician</h5>
              <ListExportToolbar
                title={isSingleDay ? "Daily Technical Report" : "Technician Duty Summary"}
                subtitleLines={filterLines}
                branchCode={branchCode}
                columns={summaryExportColumns}
                rows={summary}
                totals={summaryExportTotals}
                fileName={`TechnicianDutySummary_${stamp}`}
              />
            </div>
            <div className="table-responsive">
              <Table
                className="table datanew dataTable no-footer"
                size="small"
                columns={summaryColumnsDef}
                dataSource={summary}
                rowKey="key"
                loading={isFetching}
                pagination={false}
                locale={{ emptyText: "No technician had work in this range" }}
                onRow={(s) => ({
                  onClick: () =>
                    setTechnician((cur) =>
                      cur?.value === s.employeeRecordGuid ? null : { value: s.employeeRecordGuid, label: `${s.name} · ${s.employeeCode}` }
                    ),
                  style: {
                    cursor: "pointer",
                    background: technician?.value === s.employeeRecordGuid ? "#F7ECEE" : undefined,
                  },
                })}
                summary={() =>
                  summary.length > 1 ? (
                    <Table.Summary.Row style={{ fontWeight: 700, background: "#F8F9FA" }}>
                      <Table.Summary.Cell index={0} colSpan={2}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">{summaryTotals.serviceVisits}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">{summaryTotals.serviceRequests}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">{summaryTotals.installations}</Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">{summaryTotals.attended}</Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">{summaryTotals.open}</Table.Summary.Cell>
                      <Table.Summary.Cell index={7} />
                    </Table.Summary.Row>
                  ) : null
                }
              />
            </div>
            <small className="text-muted d-block mt-2">
              Click a technician to see only their jobs below. A job two technicians attended together counts for
              each of them here, and once in the tiles above.
            </small>
          </div>
        </div>

        {/* ---- Installations: where each one stands right now ---- */}
        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h5 className="mb-0 d-inline-flex align-items-center gap-2">
                <Tool size={18} /> Installation status
              </h5>
              <ListExportToolbar
                title="Installation Status"
                subtitle={branchCode || "All branches"}
                columns={INSTALLATION_EXPORT_COLUMNS}
                rows={installations}
                fileName={`InstallationStatus_${stamp}`}
                orientation="landscape"
              />
            </div>
            <div className="table-responsive">
              <Table
                rowKey="key"
                size="small"
                loading={isFetchingInstallations}
                dataSource={installations}
                pagination={false}
                locale={{ emptyText: "No installation jobs for this branch." }}
                columns={[
                  {
                    title: "Customer",
                    dataIndex: "customerName",
                    render: (v, r) => (
                      <>
                        <Link to={paths.salesOrderDetail(r.recordGuid)} className="fw-semibold text-decoration-none">
                          {v || "—"}
                        </Link>
                        <div className="text-muted" style={{ fontSize: 11.5 }}>
                          {r.orderNumber}
                          {isAllBranches && r.branchCode ? ` · ${r.branchCode}` : ""}
                        </div>
                      </>
                    ),
                  },
                  { title: "Started", dataIndex: "startedDate", width: 110, render: (v) => fmtDate(v) || <span className="text-muted">not started</span> },
                  {
                    title: "Expected end",
                    dataIndex: "expectedEndDate",
                    width: 130,
                    render: (v, r) =>
                      v ? (
                        <span className={r.overdue ? "text-danger fw-semibold" : undefined}>
                          {fmtDate(v)}
                          {r.overdue ? " · overdue" : ""}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      ),
                  },
                  {
                    title: "Technicians",
                    dataIndex: "technicians",
                    render: (v, r) =>
                      r.technicianCount === 0 ? (
                        <span className="text-danger">Not allocated</span>
                      ) : (
                        <>
                          <span>{v.join(", ")}</span>
                          <span className="text-muted"> ({r.technicianCount})</span>
                        </>
                      ),
                  },
                  {
                    title: "Status",
                    dataIndex: "status",
                    width: 140,
                    // Null until someone fills the order's Installation panel in -
                    // which is true of every order today, so say so rather than
                    // rendering an empty badge.
                    render: (v) =>
                      v ? (
                        <StatusBadge status={INSTALLATION_STATUS_LABEL[v] || v} />
                      ) : (
                        <span className="text-muted">Not recorded</span>
                      ),
                  },
                  {
                    title: "Elapsed",
                    dataIndex: "elapsed",
                    width: 110,
                    align: "right",
                    render: (v, r) =>
                      v === null ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <span title={r.isRunning ? "Since it started, to today" : "From start to when it finished"}>
                          {daysLabel(v)}
                          {r.isRunning ? "" : " (final)"}
                        </span>
                      ),
                  },
                  {
                    title: "Remarks",
                    dataIndex: "remark",
                    render: (v) =>
                      v ? (
                        <span style={clampStyle(2)} title={v}>
                          {v}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      ),
                  },
                ]}
                // Expand a row for the quotation / LPO / stock-out already
                // linked to that order. Reuses the panel the Order and Enquiry
                // screens use, so the targets are the same real records and a
                // browser Back returns here - no new routes, no new endpoint.
                expandable={{
                  rowExpandable: (r) => Boolean(r.enquiryRecordGuid),
                  expandedRowRender: (r) => (
                    <SalesLinkedDocumentsPanel enquiryRecordGuid={r.enquiryRecordGuid} hideKind="SALES_ENQUIRY" />
                  ),
                }}
              />
            </div>
            <small className="text-muted d-block mt-2">
              Orders carrying any service other than guarding, and where each one stands today &mdash;{" "}
              <strong>not limited to the report period above</strong>, since a job started earlier would otherwise
              disappear mid-installation. Elapsed runs to today while a job is still running, and to its completion
              once finished. Status, dates and technicians come from the order&rsquo;s own Installation panel, so
              &ldquo;Not recorded&rdquo; means nobody has filled it in yet. Expand a row for the quotation, LPO and
              stock-out already linked to that order.
            </small>
          </div>
        </div>

        {/* ---- Detail: the "Daily Work Report" ---- */}
        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h5 className="mb-0 d-inline-flex align-items-center gap-2"><ListIcon size={18} /> Job details</h5>
              <ListExportToolbar
                title={isSingleDay ? "Daily Work Report" : "Technician Work Report"}
                subtitleLines={[
                  ...filterLines,
                  show !== "ALL" && `Showing: ${SHOW_OPTIONS.find((o) => o.value === show)?.label}`,
                ].filter(Boolean)}
                branchCode={branchCode}
                columns={detailExportColumns}
                rows={exportDetailRows}
                groupBy={groupKeyFn || undefined}
                groupOrder={groupOrder}
                orientation="landscape"
                fileName={`TechnicianWorkReport_${stamp}`}
              />
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <div className="btn-group btn-group-sm" role="group" aria-label="Which jobs to show">
                {SHOW_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`btn ${show === o.value ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setShow(o.value)}
                  >
                    {o.label} <span className="ms-1 opacity-75">{showCounts[o.value]}</span>
                  </button>
                ))}
              </div>
              <select
                className="form-select form-select-sm"
                style={{ width: 200 }}
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                {GROUP_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {groupBy === "TECHNICIAN" && (
                <small className="text-muted">A shared job appears under each technician who attended it.</small>
              )}
            </div>
            <div className="table-responsive">
              <Table
                className="table datanew dataTable no-footer"
                size="small"
                columns={detailColumnsDef}
                dataSource={detailTableData}
                rowKey="rowKey"
                loading={isFetching}
                locale={{ emptyText: "No jobs match these filters" }}
                rowClassName={(r) => (r.__group ? "bg-light" : "")}
                expandable={{
                  rowExpandable: (r) => !r.__group,
                  expandedRowRender: (r) => <JobDetail row={r} />,
                }}
                pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicianDutyReportPage;
